import React, { useEffect, useState } from 'react';
import { Users, Check, Home as HomeIcon } from 'lucide-react';
import { getFamilyMembers } from '../lib/api';

const colorFromName = (name) => {
  const palette = [
    'bg-emerald-500', 'bg-blue-500', 'bg-amber-500', 'bg-purple-500',
    'bg-rose-500', 'bg-cyan-500', 'bg-orange-500', 'bg-indigo-500',
  ];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h + name.charCodeAt(i)) % palette.length;
  return palette[h];
};
const initials = (name) =>
  name.split(/\s+/).map((w) => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();

/**
 * Multi-select dei membri della famiglia come "intestatari".
 * Vuoto = movimento condiviso da tutta la famiglia (unassigned).
 *
 * Props:
 *   value: string[] (user_id[])
 *   onChange: (string[]) => void
 *   label?: string
 *   help?: string
 */
const MemberPicker = ({ value = [], onChange, label = 'Intestatari', help }) => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    getFamilyMembers().then((res) => {
      if (mounted) setMembers(res.data);
    }).catch(() => {}).finally(() => {
      if (mounted) setLoading(false);
    });
    return () => { mounted = false; };
  }, []);

  const toggle = (id) => {
    if (value.includes(id)) onChange(value.filter((v) => v !== id));
    else onChange([...value, id]);
  };

  const clearAll = () => onChange([]);

  if (loading) {
    return <div className="h-10 bg-muted rounded-lg animate-pulse" />;
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium leading-none">
          {label}
          <span className="ml-2 text-xs text-muted-foreground font-normal">
            {value.length === 0 ? '— di tutta la famiglia' : `(${value.length} selezionat${value.length > 1 ? 'i' : 'o'})`}
          </span>
        </label>
        {value.length > 0 && (
          <button
            type="button"
            onClick={clearAll}
            className="text-xs text-muted-foreground hover:text-foreground"
          >
            Tutta la famiglia
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        {/* Famiglia toggle */}
        <button
          type="button"
          onClick={clearAll}
          className={`flex items-center gap-2 rounded-full border-2 px-3 py-1.5 text-sm transition-all ${
            value.length === 0
              ? 'border-primary bg-primary/10 text-primary font-medium'
              : 'border-border bg-card hover:border-primary/40'
          }`}
          data-testid="member-picker-family"
        >
          <HomeIcon className="w-3.5 h-3.5" /> Famiglia
        </button>

        {members.map((m) => {
          const selected = value.includes(m.id);
          return (
            <button
              key={m.id}
              type="button"
              onClick={() => toggle(m.id)}
              className={`flex items-center gap-2 rounded-full border-2 pl-1 pr-3 py-0.5 text-sm transition-all ${
                selected
                  ? 'border-primary bg-primary/10 text-foreground'
                  : 'border-border bg-card hover:border-primary/40'
              }`}
              data-testid={`member-pick-${m.id}`}
            >
              <span className={`w-7 h-7 rounded-full ${colorFromName(m.name)} text-white flex items-center justify-center text-[10px] font-bold`}>
                {initials(m.name)}
              </span>
              <span className="font-medium">{m.name}</span>
              {selected && <Check className="w-3.5 h-3.5 text-primary" />}
            </button>
          );
        })}
      </div>

      {help && <p className="text-[11px] text-muted-foreground">{help}</p>}
      {value.length > 1 && (
        <p className="text-[11px] text-muted-foreground italic">
          Importo verrà <strong>diviso equamente</strong> ({value.length} parti) nei breakdown per persona.
        </p>
      )}
    </div>
  );
};

export default MemberPicker;
